import { useEffect } from 'react';
import { useTracker } from '@archipro-website/tracker';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@rocco/components/popover';
import { RadioGroup, RadioGroupItem } from '@rocco/components/radio-group';
import { Button } from '@rocco/components/button';
import { ReactComponent as ArrowChevronDownIcon } from '@rocco/icons/svg/arrow/arrow-chevron-down.svg';
import { useBoolean } from 'ahooks';
import type { DirectoryGridColumns } from '~/ui-rocco/pages/category-page/DirectoryUniformGrid';
import {
    DIRECTORY_DISPLAY_COLUMN_OPTIONS,
    isDirectoryGridColumns,
} from './directory-display-columns';

interface DirectoryDisplayDropdownProps {
    columns: DirectoryGridColumns;
    onColumnsChange: (columns: DirectoryGridColumns) => void;
    /**
     * The listing the grid shows (`products`, `projects`, …), reported as the
     * change's `Source`. Without it the page's first path segment stands in.
     */
    listing?: string;
}

/** Scroll distance that dismisses the open popover (matches SortDropdown). */
const SCROLL_CLOSE_DELTA_PX = 300;

/**
 * "Display: {n}" dropdown for the browse pages — picks how many columns the
 * desktop result grid shows. Sits beside the Sort dropdown and mirrors its
 * trigger + radio-list popover presentation, including closing after a large
 * scroll.
 */
export const DirectoryDisplayDropdown = ({
    columns,
    onColumnsChange,
    listing,
}: DirectoryDisplayDropdownProps) => {
    const tracker = useTracker();
    const [isOpen, { set: setIsOpen, setFalse: close }] = useBoolean(false);

    // Close after a large scroll. Attached only while open so the baseline is
    // the scroll position at open time — a listener attached at mount would
    // carry a stale baseline and dismiss the popover on a tiny scroll.
    useEffect(() => {
        if (!isOpen) return;
        const openScrollY = window.scrollY;
        const handleScroll = () => {
            if (
                Math.abs(window.scrollY - openScrollY) > SCROLL_CLOSE_DELTA_PX
            ) {
                close();
            }
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isOpen, close]);

    const logDisplayChange = (next: DirectoryGridColumns) => {
        const url = new URL(window.location.href);
        void tracker.log('DirectoryDisplayChange', {
            url,
            data: {
                Source: listing ?? url.pathname.split('/')[1] ?? '',
                ExtraData: JSON.stringify({ columns: next }),
            },
        });
    };

    const handleChange = (value: string) => {
        const next = Number(value);
        if (!isDirectoryGridColumns(next)) return;
        if (next !== columns) {
            logDisplayChange(next);
        }
        onColumnsChange(next);
        close();
    };

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                {/* weight matches the Sort trigger beside it (SearchSort /
                    SortDropdown) — the pair reads as one control set. */}
                <Button
                    color="transparent"
                    size="compact"
                    weight="normal"
                    className="rounded-[var(--radius-btn-m)]"
                >
                    <span>Display: {columns}</span>
                    <ArrowChevronDownIcon className="ml-2 size-4.5 transition-transform group-data-[state=open]:transform-[rotateX(180deg)]" />
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" paddings="small">
                <RadioGroup
                    aria-label="Grid columns"
                    value={String(columns)}
                    onValueChange={handleChange}
                >
                    {DIRECTORY_DISPLAY_COLUMN_OPTIONS.map((option) => (
                        <label
                            key={option}
                            className="flex cursor-pointer items-center gap-x-3 rounded-2 px-2 py-2.5 hover:bg-gray-50"
                        >
                            <RadioGroupItem value={String(option)} />
                            <span className="text-base">Display: {option}</span>
                        </label>
                    ))}
                </RadioGroup>
            </PopoverContent>
        </Popover>
    );
};
